import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  DoneAll as DoneAllIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import WhatsAppLink from '../components/common/WhatsAppLink';

interface InboxMessage {
  id: string;
  providerMessageId: string;
  fromPhone: string;
  customer: { id: string; name: string } | null;
  type: string;
  body: string | null;
  receivedAt: string;
  readAt: string | null;
}

interface InboxResponse {
  messages: InboxMessage[];
  unreadCount: number;
}

const formatRelative = (iso: string): string => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString();
};

const WhatsappInbox: React.FC = () => {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<InboxMessage | null>(null);

  const load = useCallback(async (silent: boolean = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await apiClient.get<{ data?: InboxResponse }>('/whatsapp/inbox');
      const data = res?.data;
      setMessages(data?.messages ?? []);
      setUnreadCount(data?.unreadCount ?? 0);
    } catch {
      // surface in UI: leave existing messages, indicate via toast in future
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Light polling — every 30s while the tab is open. Keeps the unread badge
  // honest without needing a websocket.
  useEffect(() => {
    const timer = setInterval(() => {
      void load(true);
    }, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const grouped = useMemo(() => {
    const map = new Map<string, InboxMessage[]>();
    for (const m of messages) {
      const list = map.get(m.fromPhone) ?? [];
      list.push(m);
      map.set(m.fromPhone, list);
    }
    // sort each thread by receivedAt desc, then sort threads by their latest
    return Array.from(map.entries())
      .map(([phone, msgs]) => {
        msgs.sort((a, b) => +new Date(b.receivedAt) - +new Date(a.receivedAt));
        return { phone, msgs };
      })
      .sort((a, b) => +new Date(b.msgs[0].receivedAt) - +new Date(a.msgs[0].receivedAt));
  }, [messages]);

  const handleSelect = async (msg: InboxMessage) => {
    setSelected(msg);
    if (!msg.readAt) {
      try {
        await apiClient.post(`/whatsapp/inbox/${msg.id}/read`, {});
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, readAt: new Date().toISOString() } : m))
        );
        setUnreadCount((n) => Math.max(0, n - 1));
      } catch {
        /* ignore */
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiClient.post('/whatsapp/inbox/read-all', {});
      const now = new Date().toISOString();
      setMessages((prev) => prev.map((m) => (m.readAt ? m : { ...m, readAt: now })));
      setUnreadCount(0);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#5C2A30' }}>
              WhatsApp Inbox
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inbound replies from customers via WhatsApp Business Cloud API.
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} unread`}
              size="small"
              sx={{ ml: 1, bgcolor: '#25D366', color: 'white', fontWeight: 700 }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={() => void load(true)} disabled={refreshing}>
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={() => void handleMarkAllRead()}
              sx={{ textTransform: 'none' }}
            >
              Mark all read
            </Button>
          )}
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#25D366' }} />
        </Box>
      ) : messages.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed rgba(0,0,0,0.12)',
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 48, color: '#25D366', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No messages yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When a customer replies to one of your WhatsApp messages, it'll show up here.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Thread list */}
          <Paper sx={{ flex: '0 0 360px', borderRadius: 3, overflow: 'hidden' }}>
            <List dense disablePadding>
              {grouped.map(({ phone, msgs }) => {
                const latest = msgs[0];
                const unread = msgs.some((m) => !m.readAt);
                return (
                  <ListItemButton
                    key={phone}
                    selected={selected?.fromPhone === phone}
                    onClick={() => void handleSelect(latest)}
                    sx={{
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                      py: 1.5,
                      bgcolor: unread ? 'rgba(37,211,102,0.05)' : undefined,
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 1.5,
                        bgcolor: 'rgba(37,211,102,0.18)',
                        color: '#1FB957',
                        fontWeight: 700,
                        width: 36,
                        height: 36,
                      }}
                    >
                      {(latest.customer?.name ?? latest.fromPhone).slice(0, 1).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: unread ? 700 : 500,
                              color: '#1F2937',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {latest.customer?.name ?? latest.fromPhone}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatRelative(latest.receivedAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {latest.body ?? `(${latest.type})`}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          {/* Conversation pane */}
          <Paper
            sx={{
              flex: 1,
              borderRadius: 3,
              minHeight: 400,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selected ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#5C2A30' }}>
                      {selected.customer?.name ?? selected.fromPhone}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selected.fromPhone}
                    </Typography>
                  </Box>
                  <WhatsAppLink
                    phone={selected.fromPhone}
                    tooltip="Reply on WhatsApp (opens wa.me)"
                  />
                </Box>
                <Box sx={{ flex: 1, mt: 2, overflowY: 'auto' }}>
                  {grouped
                    .find((g) => g.phone === selected.fromPhone)
                    ?.msgs.slice()
                    .reverse()
                    .map((m) => (
                      <Box
                        key={m.id}
                        sx={{
                          mb: 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: 'rgba(37,211,102,0.12)',
                            color: '#1F2937',
                            p: 1.25,
                            borderRadius: 2,
                            maxWidth: '80%',
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {m.body ?? `(${m.type})`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(m.receivedAt).toLocaleString()}
                          </Typography>
                          {m.readAt && <DoneIcon sx={{ fontSize: 12, color: '#1FB957' }} />}
                        </Box>
                      </Box>
                    ))}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}
                >
                  Replying from the CRM is a Stage 2.1 feature — for now use the WhatsApp icon
                  above to open this chat in WhatsApp Business App.
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">Select a conversation from the left.</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default WhatsappInbox;
